import { z } from 'zod';
import { isoDateTimeSchema } from './common';

/**
 * Staff (editorial CMS) accounts and sessions. Only staff have real authentication; children never
 * do (spec: Assumption 4). Passwords travel only in login/change requests and are never returned,
 * logged or stored except as an Argon2id hash.
 */

export const STAFF_ROLES = ['admin', 'editor'] as const;
export const staffRoleSchema = z.enum(STAFF_ROLES);
export type StaffRole = z.infer<typeof staffRoleSchema>;

export const STAFF_STATUSES = ['active', 'disabled'] as const;

export const staffUsernameSchema = z
  .string()
  .regex(/^[a-z0-9._-]{3,40}$/, 'Use 3–40 lowercase letters, digits, dots, dashes or underscores');

/** Length-based policy (NIST SP 800-63B): no composition rules, long passphrases allowed. */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const staffPasswordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);

/** Returns a policy problem or null. The username check needs both values, so it lives here. */
export function passwordPolicyProblem(username: string, password: string): string | null {
  if (!staffPasswordSchema.safeParse(password).success) return 'length';
  if (password.toLowerCase().includes(username.toLowerCase())) return 'contains-username';
  if (new Set(password).size < 5) return 'too-repetitive';
  return null;
}

/** Login input is only length-limited: format hints would help credential guessing. */
export const loginRequestSchema = z.strictObject({
  username: z.string().min(1).max(40),
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});

export const staffPrincipalSchema = z.strictObject({
  id: z.uuid(),
  username: staffUsernameSchema,
  role: staffRoleSchema,
});
export type StaffPrincipal = z.infer<typeof staffPrincipalSchema>;

/** Returned by login and GET /api/staff/session. The CSRF token goes in the X-CSRF-Token header. */
export const staffSessionResponseSchema = z.strictObject({
  user: staffPrincipalSchema,
  csrfToken: z.string().min(20),
  expiresAt: isoDateTimeSchema,
  idleTimeoutSeconds: z.int().min(60),
});
export type StaffSessionResponse = z.infer<typeof staffSessionResponseSchema>;

export const CSRF_HEADER = 'x-csrf-token';

export const staffUserSchema = z.strictObject({
  id: z.uuid(),
  username: staffUsernameSchema,
  role: staffRoleSchema,
  status: z.enum(STAFF_STATUSES),
  credentialRotatedAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
});
export type StaffUser = z.infer<typeof staffUserSchema>;

export const createStaffUserSchema = z.strictObject({
  username: staffUsernameSchema,
  role: staffRoleSchema,
  password: staffPasswordSchema,
});

export const updateStaffUserSchema = z
  .strictObject({ role: staffRoleSchema.optional(), status: z.enum(STAFF_STATUSES).optional() })
  .refine((v) => v.role !== undefined || v.status !== undefined, 'Nothing to change');

export const changePasswordSchema = z.strictObject({
  currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  newPassword: staffPasswordSchema,
});

export const resetPasswordSchema = z.strictObject({ newPassword: staffPasswordSchema });

export const auditEventSchema = z.strictObject({
  id: z.string().regex(/^\d+$/),
  actor: z.strictObject({ id: z.uuid(), username: staffUsernameSchema }).nullable(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  occurredAt: isoDateTimeSchema,
  /** Field names and states only — never passwords, tokens or free-text values. */
  changeSummary: z.record(z.string(), z.unknown()),
});
export type AuditEvent = z.infer<typeof auditEventSchema>;

export const auditQuerySchema = z.strictObject({
  targetType: z
    .string()
    .regex(/^[a-z_]{1,40}$/)
    .optional(),
  targetId: z.string().min(1).max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
